import { t } from "@lingui/macro";
import { ArrowRightIcon, CircleNotchIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@reactive-resume/ui";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { SEO } from "@/client/components/seo";
import { useLemonSqueezyOverlay } from "@/client/hooks/use-lemon-squeezy-overlay";
import { requestCheckoutUrl } from "@/client/hooks/use-upgrade-checkout";
import { rememberCheckoutAttempt } from "@/client/services/billing/last-checkout-attempt";
import { useAuthStore } from "@/client/stores/auth";
import { hasPaidPlan } from "@/client/utils/subscription";

import { RateLimitWaitlistForm } from "../_components/rate-limit-waitlist-form";
import { recordToolCtaClick } from "../_utils";
import type { FullReviewData } from "./_components/full-review-results";
import { FullReviewResults } from "./_components/full-review-results";
import type { ProfileFormValues } from "./_components/profile-input-form";
import { ProfileInputForm } from "./_components/profile-input-form";
import { ScorePreview } from "./_components/score-preview";

const LOCALSTORAGE_KEY = "linkedin-review-input";

type BillingConfig = {
  proCheckoutUrl: string;
  ultimateCheckoutUrl?: string;
};

type PageState =
  | "input"
  | "loading-preview"
  | "preview"
  | "loading-full"
  | "full-result"
  | "error"
  | "rate-limited";

type PreviewResult = { score: number; summary: string };

export const LinkedInProfileReviewPage = () => {
  const user = useAuthStore((s) => s.user);
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>("input");
  const [savedInput, setSavedInput] = useState<ProfileFormValues | undefined>();
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [fullResult, setFullResult] = useState<FullReviewData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");

  const isPaid = hasPaidPlan(user?.subscription);

  // Open the LS checkout as a top-level hosted page (the overlay 403s when
  // third-party cookies are blocked).
  const { openHosted } = useLemonSqueezyOverlay();

  // Load Lemon Squeezy billing config
  const { data: billingConfig } = useQuery<BillingConfig | null>({
    queryKey: ["billing-config"],
    queryFn: async () => {
      const { data } = await axios.get<BillingConfig | null>("/billing/config");
      return data;
    },
  });

  // On mount: restore saved input and auto-trigger full review if returnAction=full-review
  useEffect(() => {
    const savedRaw = localStorage.getItem(LOCALSTORAGE_KEY);
    if (savedRaw) {
      try {
        const parsed = JSON.parse(savedRaw) as ProfileFormValues;
        setSavedInput(parsed);
        localStorage.removeItem(LOCALSTORAGE_KEY);

        const returnAction = searchParams.get("returnAction");
        if (returnAction === "full-review" && user && isPaid) {
          void triggerFullReview(parsed);
        }
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreviewSubmit = async (values: ProfileFormValues) => {
    setSavedInput(values);
    setPageState("loading-preview");
    setErrorMessage("");

    try {
      const response = await fetch("/api/tools/linkedin-review/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (response.status === 429) {
        const msg =
          typeof payload.message === "string"
            ? payload.message
            : t`Daily preview limit reached. Try again tomorrow.`;
        setRateLimitMessage(msg);
        setPageState("rate-limited");
        return;
      }

      if (!response.ok) {
        const msg =
          typeof payload.message === "string"
            ? payload.message
            : t`Something went wrong. Please try again.`;
        setErrorMessage(msg);
        setPageState("error");
        return;
      }

      setPreviewResult(payload as PreviewResult);
      setPageState("preview");
    } catch {
      setErrorMessage(t`Something went wrong. Please try again.`);
      setPageState("error");
    }
  };

  const triggerFullReview = async (values: ProfileFormValues) => {
    setPageState("loading-full");
    setErrorMessage("");

    const token = user ? (/access_token=([^;]+)/.exec(document.cookie) ?? ["", ""])[1] : "";

    try {
      const response = await fetch("/api/tools/linkedin-review/full", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(values),
        credentials: "include",
      });

      const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (response.status === 403) {
        setErrorMessage(t`Upgrade to PRO to unlock the full review.`);
        setPageState("preview");
        return;
      }

      if (response.status === 429) {
        const msg =
          typeof payload.message === "string"
            ? payload.message
            : t`Monthly full review limit reached.`;
        setErrorMessage(msg);
        setPageState("preview");
        return;
      }

      if (!response.ok) {
        const msg =
          typeof payload.message === "string"
            ? payload.message
            : t`Something went wrong. Please try again.`;
        setErrorMessage(msg);
        setPageState("preview");
        return;
      }

      setFullResult(payload as FullReviewData);
      setPageState("full-result");
    } catch {
      setErrorMessage(t`Something went wrong. Please try again.`);
      setPageState("preview");
    }
  };

  const handleUpgrade = () => {
    if (!user) {
      // Save input and redirect to login with return URL
      if (savedInput) localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(savedInput));
      window.location.href = `/auth/login?redirect=${encodeURIComponent("/tools/linkedin-profile-review?returnAction=full-review")}`;
      return;
    }

    if (!isPaid && billingConfig?.proCheckoutUrl) {
      // Open the Lemon Squeezy checkout as a full hosted page.
      recordToolCtaClick("LINKEDIN_PROFILE_REVIEW", "upgrade_cta");
      if (savedInput) localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(savedInput));
      const url = new URL(billingConfig.proCheckoutUrl);
      if (user.email) url.searchParams.set("checkout[email]", user.email);
      if (user.id) url.searchParams.set("checkout[custom][user_id]", user.id);
      url.searchParams.set("checkout[custom][source]", "linkedin_review_tool");
      const fallbackUrl = url.toString();
      // Bind the checkout to the exact (monthly) PRO variant — LS ignores
      // the static URL's `?variant=` param and defaults to Monthly anyway,
      // but going through the same API as every other surface keeps this
      // tool consistent and future-proof if a cycle picker is ever added here.
      // Monthly always resolves `ok: true` (it has a safe static fallback —
      // see `requestCheckoutUrl`'s doc comment), so there is no error branch
      // to handle here the way weekly/yearly surfaces must.
      void requestCheckoutUrl("PRO", "monthly", fallbackUrl, "hosted", "linkedin_review_tool").then(
        (result) => {
          if (result.ok) {
            void Promise.resolve(
              openHosted(result.url, {
                surface: "linkedin_review_tool",
                plan: "PRO",
                interval: "monthly",
                attempt_id: result.attemptId,
              }),
            ).then((opened) => {
              if (opened)
                rememberCheckoutAttempt({ tier: "PRO", interval: "monthly" }, user.id, {
                  mode: "hosted",
                  opened: true,
                });
            });
          }
        },
      );
    }
  };

  const renderUpgradeCta = () => {
    if (!user) {
      return (
        <div className="mt-4 flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">{t`Sign up to unlock the full section-by-section review and rewrite suggestions.`}</p>
          <Button
            onClick={() => {
              recordToolCtaClick("LINKEDIN_PROFILE_REVIEW", "upgrade_cta");
              handleUpgrade();
            }}
          >
            {t`Sign up & unlock full review`}
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      );
    }

    if (!isPaid) {
      return (
        <div className="mt-4 flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">{t`Upgrade to PRO to unlock the full section-by-section review and rewrite suggestions.`}</p>
          <Button
            onClick={() => {
              recordToolCtaClick("LINKEDIN_PROFILE_REVIEW", "upgrade_cta");
              handleUpgrade();
            }}
          >
            {t`Upgrade to PRO`}
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </div>
      );
    }

    return (
      <div className="mt-4">
        <Button
          onClick={() => {
            if (savedInput) void triggerFullReview(savedInput);
          }}
        >
          {t`Get Full Review`}
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  };

  const renderPostReviewCta = () => (
    <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border p-6">
      <p className="font-semibold">{t`Ready to take your job search further?`}</p>
      <p className="text-center text-sm text-muted-foreground">{t`Apply Edge helps you automate job applications, tailor your resume, and track everything in one place.`}</p>
      <Button
        asChild
        onClick={() => {
          recordToolCtaClick("LINKEDIN_PROFILE_REVIEW", "post_review_cta");
        }}
      >
        <Link to="/dashboard">
          {t`Continue with Apply Edge`}
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </Link>
      </Button>
    </div>
  );

  return (
    <>
      <SEO
        title={t`Free LinkedIn Profile Review | Apply Edge`}
        description={t`Get an AI-powered review of your LinkedIn profile. Optimize your headline, summary, and skills to attract more recruiters.`}
        canonical="/tools/linkedin-profile-review"
      />

      <div className="min-h-screen bg-background px-4 pb-24 pt-32 delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both sm:px-6 sm:pt-36">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold sm:text-4xl">{t`LinkedIn Profile Review`}</h1>
            <p className="mt-2 text-lg text-muted-foreground">
              {t`Get an instant score and find out what's holding your profile back.`}
            </p>
          </div>

          {/* Error banner (non-rate-limit errors, shown above form) */}
          {(pageState === "error" || (pageState === "preview" && errorMessage)) && (
            <div className="bg-destructive/10 text-destructive mb-6 flex items-start gap-3 rounded-xl p-4">
              <WarningCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">{errorMessage}</p>
              </div>
              {pageState === "error" && savedInput && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPageState("input");
                    setErrorMessage("");
                  }}
                >
                  {t`Try Again`}
                </Button>
              )}
            </div>
          )}

          {/* Rate limit */}
          {pageState === "rate-limited" && (
            <Card className="shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardHeader>
                <CardTitle>{t`Daily limit reached`}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-muted-foreground">{rateLimitMessage}</p>
                <RateLimitWaitlistForm tool="LINKEDIN_PROFILE_REVIEW" />
              </CardContent>
            </Card>
          )}

          {/* Input form */}
          {(pageState === "input" || pageState === "error") && (
            <Card className="shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardContent className="pt-6">
                <ProfileInputForm
                  defaultValues={savedInput}
                  isLoading={false}
                  onSubmit={(values) => {
                    void handlePreviewSubmit(values);
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Loading preview */}
          {pageState === "loading-preview" && (
            <div className="flex flex-col items-center gap-4 py-16">
              <CircleNotchIcon className="h-10 w-10 animate-spin opacity-60" />
              <p className="text-muted-foreground">{t`Analyzing your profile...`}</p>
            </div>
          )}

          {/* Score preview */}
          {pageState === "preview" && previewResult && (
            <Card className="shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardContent className="pt-6">
                <ScorePreview
                  score={previewResult.score}
                  summary={previewResult.summary}
                  ctaSlot={renderUpgradeCta()}
                />
                {errorMessage && (
                  <p className="text-destructive mt-4 text-center text-sm">{errorMessage}</p>
                )}
                <div className="mt-6 text-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setPageState("input");
                      setErrorMessage("");
                    }}
                  >
                    {t`Review a different profile`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading full review */}
          {pageState === "loading-full" && (
            <div className="flex flex-col items-center gap-4 py-16">
              <CircleNotchIcon className="h-10 w-10 animate-spin opacity-60" />
              <p className="text-muted-foreground">{t`Generating your full review...`}</p>
            </div>
          )}

          {/* Full review */}
          {pageState === "full-result" && fullResult && (
            <Card className="shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardContent className="pt-6">
                <FullReviewResults ctaSlot={renderPostReviewCta()} review={fullResult} />
                <div className="mt-6 text-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setPageState("input");
                      setErrorMessage("");
                      setFullResult(null);
                      setPreviewResult(null);
                    }}
                  >
                    {t`Review a different profile`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};
